// FILE: src/Components/GroupsProfile.js (FULLY UPDATED)

import { useEffect, useState } from "react";
import { Input, Modal, Spin, Dropdown, Button, Tooltip, message } from "antd";
import {
EditOutlined,
DeleteOutlined,
EllipsisOutlined,
LoadingOutlined,
ExclamationCircleFilled,
LinkOutlined
} from "@ant-design/icons";
import {
deleteGroupName,
deleteConnectedProfile,
updateGroupName,
reconnectFacebookProfile,
reconnectPinterestProfile,
reconnectInstagramProfile,
reconnectLinkedInProfile,
reconnectLinkedInBusinessProfile,
reconnectYouTubeProfile,
reconnectTikTokProfile,
} from "../services/user.profile";
import {
fetchGroupDetails,
fetchGroups,
fetchAllProfileDetails,
setSelectedGroup,
removeConnectedProfileFromSelectedGroup,
} from "../store/Slices/GroupSlice";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import CreateBrandWorkflowModal from "./CreateGroup/CreateBrandWorkflowModal";
import ConnectProfileModal from "./ConnectProfileModal";
import { renderPlatformIcon, renderProfileImage, renderProfileName } from './ShowProfiles';



const GroupCard = ({ group, isActive, onSelect, onEdit, onDelete }) => {
const menuItems = [
{ key: 'edit', label: 'Edit Name', icon: <EditOutlined />, onClick: () => onEdit(group) },
{ key: 'delete', label: 'Delete Group', icon: <DeleteOutlined />, danger: true, onClick: () => onDelete(group) }
];

return (
<div className={`relative p-3 rounded-lg shadow-sm flex items-center justify-between min-w-[200px] transition-all duration-200 cursor-pointer ${isActive ? 'bg-brand-primary text-white shadow-md' : 'bg-gray-100 hover:bg-gray-200'}`} onClick={() => onSelect(group.name)}>
<div className="font-medium flex-grow flex items-center gap-2">
{group.status === 'pending' ? <Spin size="small" /> : <i className="fa fa-folder" />}
<span className="truncate">{group.name}</span>
</div>
<Dropdown menu={{ items: menuItems }} trigger={['click']} placement="bottomRight">
<Button
type="text"
shape="circle"
icon={<EllipsisOutlined className={isActive ? 'text-white' : 'text-gray-500'} />}
onClick={(e) => e.stopPropagation()}
/>
</Dropdown>
</div>
);
};

const ProfileListItem = ({ profile, profileDetails, onDelete, onReconnect }) => {
const hasError = profileDetails?.error === 'TOKEN_INVALID';

return (
<div className={`flex justify-between items-center bg-white p-3 rounded-lg border hover:shadow-sm transition-shadow duration-200 ${hasError ? 'border-red-300 bg-red-50' : 'border-gray-200'}`}>
<div className="flex items-center space-x-4">
{renderProfileImage(profile)}
<div className="flex items-center space-x-2 font-medium text-gray-800">
{renderPlatformIcon(profile.platform)}
{renderProfileName(profile)}
{hasError &&
<Tooltip title="This connection has expired and requires a refresh.">
<ExclamationCircleFilled className="text-red-500" />
</Tooltip>
}
</div>
</div>
<div className="flex items-center space-x-2">
{hasError && (
<Button
type="primary"
size="small"
danger
onClick={() => onReconnect(profile)}
>
Reconnect
</Button>
)}
<Tooltip title="Remove from Group">
<Button type="text" shape="circle" danger icon={<i className="fas fa-trash-alt"></i>} onClick={() => onDelete(profile)} />
</Tooltip>
</div>
</div>
);
};

export default function GroupsProfile() {
const dispatch = useDispatch();

const { groups, selectedGroup, profile: connectedProfiles, loading, profileDetails } = useSelector(
(state) => state.groups
);

const [isConnectModalOpen, setIsConnectModalOpen] = useState(false);
const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

const [editGroup, setEditGroup] = useState(null);
const [editModalVisible, setEditModalVisible] = useState(false);
const [editGroupName, setEditGroupName] = useState("");
const [editLoading, setEditLoading] = useState(false);

const antIcon = <LoadingOutlined style={{ fontSize: 24, color: "#0B968F" }} spin />;

useEffect(() => {
const loadData = async () => {
const groupsAction = await dispatch(fetchGroups());
const fetchedGroups = groupsAction.payload || [];
const currentSelectedGroup = localStorage.getItem("selected_group");
const activeGroups = fetchedGroups.filter(g => g.status !== 'pending');

if (!currentSelectedGroup && activeGroups.length > 0) {
dispatch(setSelectedGroup(activeGroups[0].name));
} else if (currentSelectedGroup && !activeGroups.some(g => g.name === currentSelectedGroup)) {
dispatch(setSelectedGroup(activeGroups.length > 0 ? activeGroups[0].name : null));
}
};
loadData();
}, [dispatch]);

useEffect(() => {
if (selectedGroup) {
dispatch(fetchGroupDetails(selectedGroup));
}
if (groups && groups.length > 0) {
dispatch(fetchAllProfileDetails());
}
}, [selectedGroup, groups, dispatch]);

useEffect(() => {
if (editGroup) setEditGroupName(editGroup.name);
}, [editGroup]);

const handleSelectGroup = (groupName) => {
if (groupName !== selectedGroup) {
dispatch(setSelectedGroup(groupName));
}
};

const handleDeleteProfile = async (profileToDelete) => {
Modal.confirm({
title: "Remove Profile",
content: `Are you sure you want to remove "${profileToDelete.profile || profileToDelete.platform_id}" from the "${selectedGroup}" group?`,
okText: "Remove",
okType: "danger",
onOk: () => {
const groupNameForDelete = selectedGroup;

dispatch(
removeConnectedProfileFromSelectedGroup({
groupName: groupNameForDelete,
profile: profileToDelete,
})
);

toast.success("Profile removed. Cleanup is running in the background.");

deleteConnectedProfile({
profile: profileToDelete,
groupName: groupNameForDelete,
})
.then(() => {
dispatch(fetchGroups());
dispatch(fetchAllProfileDetails());
})
.catch(() => {
toast.error("Failed to remove profile. Restoring latest data.");
dispatch(fetchGroups());
dispatch(fetchGroupDetails(groupNameForDelete));
dispatch(fetchAllProfileDetails());
});
},
});
};

const handleEditSave = async () => {
if (!editGroupName.trim()) {
toast.error("Group name cannot be empty.");
return;
}
setEditLoading(true);
try {
const oldName = editGroup.name;
await updateGroupName(oldName, editGroupName);
toast.success("Group name updated successfully!");

await dispatch(fetchGroups());

if (oldName === selectedGroup) {
dispatch(setSelectedGroup(editGroupName));
}

setEditModalVisible(false);
} catch (error) {
toast.error("Failed to update group name.");
} finally {
setEditLoading(false);
}
};

const handleDeleteGroup = (groupToDelete) => {
if ((groups?.length || 0) <= 1) {
toast.error("You must keep at least one brand in your account. Delete your account instead if you want to remove everything.");
return;
}

Modal.confirm({
title: `Delete the "${groupToDelete.name}" brand?`,
icon: <ExclamationCircleFilled />,
content: (
<div>
<p className="font-semibold text-red-600">This action is permanent and cannot be undone.</p>
<p className="mt-2">This will permanently delete:</p>
<ul className="list-disc list-inside text-sm text-gray-600 mt-1">
<li>The Brand / Group itself</li>
<li>The Onboarding & Brand Kit profile</li>
<li>All media in the Library (including files in cloud storage)</li>
<li>All scheduled posts and drafts</li>
<li>All published post history for this brand</li>
</ul>
</div>
),
okText: 'Yes, Delete Everything',
okType: 'danger',
cancelText: 'Cancel',
onOk: async () => {
try {
await deleteGroupName(groupToDelete.name);
toast.success(`Brand "${groupToDelete.name}" and all its data have been deleted.`);
const remainingGroupsAction = await dispatch(fetchGroups());
const remainingGroups = remainingGroupsAction.payload;
if (selectedGroup === groupToDelete.name) {
if (remainingGroups && remainingGroups.length > 0) {
dispatch(setSelectedGroup(remainingGroups[0].name));
} else {
dispatch(setSelectedGroup(null));
}
}
} catch (error) {
toast.error(error.message || "Failed to delete brand.");
}
},
});
};

const handleReconnect = async (profileToReconnect) => {
const platform = profileToReconnect?.platform;
const platformId = profileToReconnect?.platform_id;

if (!platform || !platformId) {
toast.error("Cannot reconnect: Profile platform or ID is missing.");
return;
}

const openReconnectPopup = (authUrl, popupTitle, successLabel) => {
const popup = window.open(authUrl, popupTitle, 'width=1000,height=700');
if (!popup) {
toast.error("Popup blocked. Please allow popups and try again.");
return;
}

const messageListener = (event) => {
if (event.origin !== window.location.origin) return;
if (event.data === 'social_auth_success') {
if (popup) popup.close();
clearInterval(popupWatcher);
toast.success(`${successLabel} reconnected successfully! Refreshing data...`);
window.removeEventListener('message', messageListener, false);
dispatch(fetchGroupDetails(selectedGroup));
dispatch(fetchAllProfileDetails());
}
};

window.addEventListener('message', messageListener, false);

const popupWatcher = setInterval(() => {
if (popup.closed) {
clearInterval(popupWatcher);
window.removeEventListener('message', messageListener, false);
}
}, 500);
};

try {
if (platform === 'FACEBOOK') {
toast.info("Preparing to reconnect Facebook. A popup will appear.");
const { authUrl } = await reconnectFacebookProfile(platformId);
openReconnectPopup(authUrl, 'Facebook Reconnect', 'Facebook');
return;
}

if (platform === 'PINTEREST') {
toast.info("Redirecting to Pinterest reconnect...");
const { authUrl } = await reconnectPinterestProfile(platformId);
// Pinterest redirect flow is handled by /PintRedirect in the frontend.
window.location.href = authUrl;
return;
}

if (platform === 'INSTAGRAM') {
toast.info("Redirecting to Instagram reconnect...");
const { authUrl } = await reconnectInstagramProfile(platformId);
// Instagram redirect flow is handled by /instaRedirect in the frontend.
window.location.href = authUrl;
return;
}

if (platform === 'LINKEDIN') {
toast.info("Preparing to reconnect LinkedIn. A popup will appear.");
const { authUrl } = await reconnectLinkedInProfile(platformId);
openReconnectPopup(authUrl, 'LinkedIn Reconnect', 'LinkedIn');
return;
}

if (platform === 'LINKEDIN_COMPANY') {
toast.info("Preparing to reconnect LinkedIn Company Page. A popup will appear.");
const { authUrl } = await reconnectLinkedInBusinessProfile(platformId);
openReconnectPopup(authUrl, 'LinkedIn Company Reconnect', 'LinkedIn Company Page');
return;
}

if (platform === 'YOUTUBE') {
toast.info("Preparing to reconnect YouTube. A popup will appear.");
const { authUrl } = await reconnectYouTubeProfile(platformId);
openReconnectPopup(authUrl, 'YouTube Reconnect', 'YouTube');
return;
}

if (platform === 'TIKTOK') {
toast.info("Redirecting to TikTok reconnect...");
const { authUrl } = await reconnectTikTokProfile(platformId);
// TikTok reconnect uses the full-page redirect flow handled by /TiktokRedirect.
window.location.href = authUrl;
return;
}

toast.error(`${platform} reconnect is not supported from this screen yet.`);
} catch (error) {
toast.error(error.message || `Failed to initiate ${platform} reconnect.`);
}
};

const handleBrandCreateComplete = (newGroupName) => {
  if (newGroupName) {
    dispatch(setSelectedGroup(newGroupName));
  }

  setIsCreateModalOpen(false);
};

return (
<>
<div className="p-4 sm:p-6 lg:p-8 bg-gray-50 min-h-screen">
<header className="flex flex-wrap items-center justify-between mb-8">
<h1 className="text-3xl font-bold text-gray-800">Brands & Channels</h1>
<div className="flex gap-3 mt-2 md:mt-0">
<Button onClick={() => setIsCreateModalOpen(true)}>Create a Brand</Button>
</div>
</header>

<section className="bg-white p-6 rounded-xl shadow-lg border mb-8">
<h2 className="text-xl font-semibold text-gray-700 mb-4">Your Brands</h2>
{loading && groups.length === 0 ? (
<div className="w-full flex justify-center py-4"><Spin indicator={antIcon} /></div>
) : groups.length > 0 ? (
<div className="flex flex-wrap gap-4">
{groups.map((group) => (
<GroupCard
key={group.name}
group={group}
isActive={selectedGroup === group.name}
onSelect={handleSelectGroup}
onEdit={(groupToEdit) => { setEditGroup(groupToEdit); setEditGroupName(groupToEdit.name); setEditModalVisible(true); }}
onDelete={handleDeleteGroup}
/>
))}
</div>
) : (
<div className="text-center py-8 bg-gray-50 rounded-lg">
<p className="text-gray-500">You haven't created any brands yet.</p>
<Button type="primary" className="mt-4" onClick={() => setIsCreateModalOpen(true)}>Create Your First Brand</Button>
</div>
)}
</section>

<section className="bg-white p-6 rounded-xl shadow-lg border">
<div className="flex justify-between items-center mb-4">
<h2 className="text-xl font-semibold text-gray-700">{connectedProfiles?.length || 0} Connected Profile{connectedProfiles?.length !== 1 && "s"}</h2>
<Button type="primary" icon={<i className="fa fa-plus mr-2" />} onClick={() => setIsConnectModalOpen(true)}>Connect a Profile</Button>
</div>

{loading ? (
<div className="w-full flex justify-center py-4"><Spin indicator={antIcon} /></div>
) : connectedProfiles && connectedProfiles.length > 0 ? (
<div className="space-y-3">
{connectedProfiles.map((profile, idx) => {
const key = profile.platform === 'FACEBOOK' ? profile.platform_pageId : profile.platform_id;
return (
<ProfileListItem
key={`${profile.platform_id}-${profile.platform_pageId}-${idx}`}
profile={profile}
profileDetails={profileDetails[key]}
onDelete={handleDeleteProfile}
onReconnect={handleReconnect}
/>
)
})}
</div>
) : (
<div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed">
<div className="flex justify-center items-center mb-4">
<LinkOutlined className="text-4xl text-gray-300" />
</div>
<h3 className="text-lg font-semibold text-gray-700">No Channels Connected</h3>
<p className="text-gray-500 mt-1 max-w-sm mx-auto">Connect your social media profiles to start managing your content and viewing analytics for this brand.</p>
<Button type="primary" className="mt-6" onClick={() => setIsConnectModalOpen(true)}>Connect Your First Channel</Button>
</div>
)}
</section>
</div>

<ConnectProfileModal
isOpen={isConnectModalOpen}
onClose={() => setIsConnectModalOpen(false)}
/>

<CreateBrandWorkflowModal isOpen={isCreateModalOpen} onClose={() => setIsCreateModalOpen(false)} onBrandCreateComplete={handleBrandCreateComplete} />

<Modal
title={ <div className="flex items-center gap-2"> <EditOutlined /> <span className="font-semibold">Edit Group Name</span> </div> }
open={editModalVisible}
onCancel={() => setEditModalVisible(false)}
onOk={handleEditSave}
okText="Save Changes"
cancelText="Cancel"
okButtonProps={{ loading: editLoading }}
centered
destroyOnClose
>
<div className="py-4 space-y-4">
<p className="text-sm text-gray-500"> Update the name of your group. This change will be reflected across the platform. </p>
<div>
<label className="block text-sm font-medium text-gray-700 mb-1"> Group Name </label>
<Input value={editGroupName} onChange={(e) => setEditGroupName(e.target.value)} placeholder="Enter a new, unique group name" onPressEnter={handleEditSave} />
</div>
</div>
</Modal>
</>
);
}